!customElements.get("po-rtr-single") && customElements.define("po-rtr-single", class extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    this.rtrViewer = window.rtrViewer;
    this.camera = null;
    this.isRtrStarted = false;
    this.isRtrRendered = false;
    this.progressBar = this.querySelector("po-progress-indicator");
    this.staticImage = this.querySelector("[data-static-img]");
    this.packagingImage = this.querySelector("[data-packaging-img]");
    if (!this.rtrViewer) {
      console.error("RTRViewer is not available.");
      return;
    }

    this.containerSel = this.dataset.containerSelector;
    this.container = this.containerSel ? this.querySelector(this.containerSel) : null;
    this.desktopBreakpoint = parseInt(this.dataset.desktopBreakpoint) || 1024;
    this.stopTimeoutDelay = parseInt(this.dataset.stopTimeoutDelay) || 0;

    if (!this.container) {
      console.error(`Container ${this.containerSel} not found.`);
      return;
    }

    this.variantBarcode = this.container.dataset.defaultVariantBarcode;
    this.desktopBackground = this.formatImageUrl(this.container.dataset.desktopBackground);
    this.mobileBackground = this.formatImageUrl(this.container.dataset.mobileBackground);
    this.defaultBackground = this.formatImageUrl(this.container.dataset.defaultBackground);
    this.showBackground = this.container.dataset.showBackground === "true";

    // Blocks
    this.contentBlocks = this.querySelectorAll("[data-content-block-id]");

    // Swiper
    this.swiperElement = this.querySelector(`.swiper`);

    if (this.swiperElement) {
      let attrValue = this.swiperElement.getAttribute('data-slide-for-view');
      this.slideForView = attrValue !== null ? parseInt(attrValue, 10) : "auto";
    }

    this.slides = this.querySelectorAll('.swiper-slide');
    this.currSlide = this.slides[0];

    this.pagination = this.querySelectorAll('.swiper-pagination-bullet');
    if (this.pagination) {
      this.pagination.forEach(pagination => {
        pagination.addEventListener('click', this.pagination_clickHandler);
      });
    }

    this.slides.forEach(slide => {
      slide.addEventListener('click', this.slide_clickHandler);

    });

    // Positions
    this.rtrPositions = JSON.parse(this.querySelector("[data-rtr-position-obj]")?.textContent.trim() || "{}");

    // Init object with settings
    this.initInput = {
      data: {
        selector: this.containerSel,
        id: {
          type: 'upc', // moco or token
          value: this.variantBarcode, // or any other valid UPC, MoCo or token
        },
        backgroundPath: this.defaultBackground,
        settings: {
          autoResetCamera: true,
          autoRotateCamera: false,
          autoRotateSpeed: 20,
          backgroundFit: 'fill',
          boundingArea: {
            topLeft: {
              x: 0,
              y: 0,
            },
            bottomRight: {
              x: 1,
              y: 1,
            },
          },
          boundingFit: 'auto',
          cameraAnimationTime: 0.3,
          cameraRotationInitial: {
            phi: 0,
            theta: 0,
          },
          cameraRotationReset: {
            phi: 10,
            theta: 30,
          },
          clearColor: '#F5F5F5',
          gestures: {
            mouse: {
              left: 'rotate',
              middle: 'none',
              right: 'pan',
              wheel: 'none',
            },
            touches: {
              one: 'rotate',
              two: 'dolly-pan',
              three: 'none',
            },
          },
          lensesName: undefined,
          lensesOpacity: 1,
          orbitPoint: true,
          qualityMoving: 4,
          qualityStill: 5,
          scrollInteraction: false,
          showBackground: this.showBackground,
          showEnvironment: false,
          transmission: true,
        }
      },
      callbacks: {
        onRendered: () => this.handleOnRendered(),
        onError: (error) => console.error("RTRViewer error:", error),
      },
    };

    window.addEventListener('resize', this.window_resizeHandler);

    if (this.isDesktop()) {
      // Desktop BoundingArea
      this.initInput.data.settings.boundingArea.topLeft.x = 0;
      this.initInput.data.settings.boundingArea.bottomRight.y = 1;
      // Desktop BackgroundPath
      this.initInput.data.backgroundPath = this.desktopBackground || this.defaultBackground;
    } else {
      this.initInput.data.backgroundPath = this.mobileBackground || this.desktopBackground || this.defaultBackground;
    }

    this.buttonSwiper = window.Swiper ? new window.Swiper(this.swiperElement, {
      slidesPerView: this.slideForView,
      slidesPerGroupAuto: true,
      navigation: {
        nextEl: '[data-swiper-btn-next]',
        prevEl: '[data-swiper-btn-prev]',
      },
      updateOnWindowResize: true,
      loop: false,
      grabCursor: true,
      centeredSlidesBounds: true,
      simulateTouch: true,
      slideToClickedSlide: true
    }) : null;

    this.stopTimeoutId = null;

    this.intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.startRtr();
          if (this.progressBar) {
            this.progressBar.startLoading();
          }
        } else {
          this.scheduleRtrStop();
        }
      });
    });

    this.intersectionObserver.observe(this.container);

    /** When rtr-features-wrapper is 90% visible: remove rtr-overlay and overlay-only DOM nodes */
    this.featuresWrapper = this.querySelector(".rtr-features-wrapper");
    this.containerWrapper = this.querySelector(".rtr-container-wrapper");
    if (this.classList.contains("rtr-overlay") && this.featuresWrapper) {
      this.layoutObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.intersectionRatio < 0.9) return;
            /* One-way: remove overlay once, then stop observing to avoid layout/animation loop */
            this.classList.remove("rtr-overlay");
            /* No longer scrollable: drop the keyboard tab-stop added for a11y (SIA-R84) */
            this.removeAttribute("tabindex");
            this.removeAttribute("role");
            this.removeAttribute("aria-label");
            this.removeOverlayContent();
            this.selectFirstSlide();
            if (this.layoutObserver && this.featuresWrapper) {
              this.layoutObserver.unobserve(this.featuresWrapper);
              this.layoutObserver.disconnect();
              this.layoutObserver = null;
            }
          }
        },
        { root: this, rootMargin: "0px", threshold: 0.9 }
      );
      this.layoutObserver.observe(this.featuresWrapper);
    }

    if (!this.buttonSwiper) {
      console.warn("Swiper is not available.");
    }
  }

  disconnectedCallback() {
    this.slides?.forEach(slide => {
      slide.removeEventListener('click', this.slide_clickHandler);
    });

    window.removeEventListener('resize', this.window_resizeHandler);
    this.intersectionObserver?.disconnect();
    if (this.layoutObserver && this.featuresWrapper) {
      this.layoutObserver.unobserve(this.featuresWrapper);
      this.layoutObserver.disconnect();
    }

    if (this.isRtrStarted) {
      this.stopRtr();
    }

    this.buttonSwiper?.destroy();
  }

  formatImageUrl(path) {
    return path ? URL.parse(path, window.location.origin)?.toString() : null;
  }

  handleOnRendered() {
    this.camera = this.rtrViewer.getCameraControls();

    if (this.progressBar) {
      this.progressBar.setAttribute("value", this.progressBar.getAttribute("max") || "100");
    }
    this.classList.remove("rtr-loading");

    if (this.slides.length > 0) {
      this.slides.forEach((slide, index) => {
        slide.toggleAttribute("data-active", index === 0);
      });

      // Only handle slide change if not in rtr-overlay mode (use default position)
      if (!this.classList.contains("rtr-overlay")) {
        this.handleSlideChange(this.currSlide);
        //this.rtrViewer.setClearColor('#ffffff'); // Ensure clearColor is set to white after rendering in overlay mode
      }
    } else {
      this.contentBlocks.forEach((block, index) => {
        block.toggleAttribute("data-active", index === 0);
      });
    }

    this.isRtrRendered = true;

    this.activityCallback(
      () => this.classList.add('rtr-suspended'),
      () => this.classList.remove('rtr-suspended'),
      10000, this);

    this.rotationCallback(5000, this);
    this.staticImage?.classList.add('tw-hidden');

  }

  moveCameraToCustomPosition(slide) {
    let pos, target;
    const isDesktop = this.isDesktop();
    const isTablet = this.isTablet();
    const posDesktop = slide.dataset.pos;
    const targetDesktop = slide.dataset.target;
    const posTablet = slide.dataset.posTablet;
    const targetTablet = slide.dataset.targetTablet;
    const posMob = slide.dataset.posMob;
    const targetMob = slide.dataset.targetMob;

    const parseCoords = str => (str || '0,0,0').split(',').map(Number);
    const validCoords = coords => coords?.trim();

    if (isDesktop && validCoords(posDesktop) && validCoords(targetDesktop)) {
      pos = parseCoords(posDesktop);
      target = parseCoords(targetDesktop);
    }
    else if (isTablet && validCoords(posTablet) && validCoords(targetTablet)) {
      pos = parseCoords(posTablet);
      target = parseCoords(targetTablet);
    }
    else {
      pos = parseCoords(posMob);
      target = parseCoords(targetMob);
    }

    console.log('Moving camera to position :', pos, 'and target:', target);
    if (!this.camera) {
      console.warn("Camera not initialized.");
      return;
    }

    this.camera.setLookAt(pos[0], pos[1], pos[2], target[0], target[1], target[2], true);

  }

  isPackagingSlide(slide) {
    return slide?.dataset?.slideType === "packaging";
  }

  togglePackagingOverlay(slide) {
    if (!this.packagingImage) return;

    const shouldShowPackaging = this.isPackagingSlide(slide);
    this.packagingImage.classList.toggle("tw-hidden", !shouldShowPackaging);
    this.classList.toggle("packaging-active", shouldShowPackaging);
  }

  handleSlideChange(slide) {
    if (!slide) {
      return;
    }

    this.currSlide = slide;
    this.updateContentBlock(slide.dataset.blockId);
    this.togglePackagingOverlay(slide);

    if (!this.isPackagingSlide(slide)) {
      this.moveCameraToCustomPosition(slide);
    }
  }


  pagination_clickHandler = () => {
    if (!this.isRtrRendered) {
      return;
    }

    const currSlide = this.querySelector('.swiper-slide-active');

    if (!currSlide) {
      console.warn("Current slide not found.");
      return;
    }

    this.slides.forEach(slide => {
      slide.toggleAttribute("data-active", slide === currSlide);
    });

    this.handleSlideChange(currSlide);
    this.buttonSwiper?.slideTo(this.buttonSwiper.clickedIndex);
  }

  slide_clickHandler = (event) => {
    let currSlide;

    if (!this.isRtrRendered) {
      return;
    }

    this.slides.forEach(slide => {
      if (slide.toggleAttribute("data-active", slide === event.currentTarget)) {
        currSlide = slide;
      }
    });

    if (!currSlide) {
      console.warn("Current slide not found.");
      return;
    }

    this.handleSlideChange(currSlide);

    this.buttonSwiper?.slideTo(this.buttonSwiper.clickedIndex); // Ensures direct navigation
  }

  updateContentBlock(currentBlockId) {
    this.contentBlocks.forEach(block => {
      block.toggleAttribute("data-active", block.dataset.contentBlockId === currentBlockId);
    });
  }

  /**
   * Removes overlay-only nodes from the DOM when switching to side-by-side layout.
   */
  removeOverlayContent() {
    if (!this.containerWrapper) return;
    const topEl = this.containerWrapper.querySelector(".rtr-overlay--top-content");
    const bottomEl = this.containerWrapper.querySelector(".rtr-overlay--bottom-content");
    topEl?.remove();
    bottomEl?.remove();
  }

  /**
   * Selects the first slide on layout change (rtr-overlay removed): updates data-active,
   * syncs Swiper and runs handleSlideChange so moveCameraToCustomPosition runs for the first slide.
   */
  selectFirstSlide() {
    if (!this.slides?.length) return;
    const firstSlide = this.slides[0];
    this.slides.forEach((slide, index) => {
      slide.toggleAttribute("data-active", index === 0);
    });
    if (this.buttonSwiper) {
      this.buttonSwiper.slideTo(0);
    }
    // Update camera position for the first slide
    this.handleSlideChange(firstSlide);
  }

  isDesktop() {
    return window.innerWidth >= this.desktopBreakpoint;
  }

  isTablet() {
    return window.innerWidth < this.desktopBreakpoint && window.innerWidth >= 768;
  }

  startRtr() {
    if (this.stopTimeoutId) {
      clearTimeout(this.stopTimeoutId);
    }
    if (window.rtrViewerMV) {
      window.rtrViewerMV.isInitialized()
        .then(initialized => {
          if (initialized) {
            window.rtrViewerMV.dispose();
          }
        })
        .catch(err => {
          console.error("Error initializing rtrViewerMV:", err);
        });
    }
    if (this.rtrViewer.isInitialized()) {
      if (this.isRtrStarted) return;
      this.rtrViewer.dispose();
    }

    this.rtrViewer.init(this.initInput);
    this.isRtrStarted = true;
  }

  stopRtr() {
    if (this.isRtrStarted) {
      this.rtrViewer.dispose();
      this.isRtrStarted = false;
      this.isRtrRendered = false;
    }
  }

  activityCallback(inactivityCallback, activityCallback, timeout = 3000, container) {
    let timer;
    const activityEvents = ['mousemove', 'keydown', 'click', 'touchend'];

    const resetTimer = () => {
      clearTimeout(timer);
      timer = setTimeout(inactivityCallback, timeout);
      if (typeof activityCallback === "function") activityCallback()
    };

    const addEvents = () => activityEvents.forEach(event => { container.addEventListener(event, resetTimer) });
    const removeEvents = () => activityEvents.forEach(event => container.removeEventListener(event, resetTimer));

    if (container) {
      const observer = new IntersectionObserver(entries => entries.forEach(entry => entry.isIntersecting ? addEvents() : removeEvents()));
      observer.observe(container);
    } else {
      addEvents();
    }


    resetTimer();
  }

  rotationCallback(timeout, container) {
    let timer;
    this.back = true;

    const resetTimer = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        if (this.back && this.currSlide && !this.isPackagingSlide(this.currSlide) && !this.classList.contains("rtr-overlay")) {
          this.moveCameraToCustomPosition(this.currSlide);
        }
      }, timeout);
    }

    const mouseDown = () => {
      this.back = false;
      resetTimer();
    }

    const mouseUp = () => {
      this.back = true;
      resetTimer();
    }

    const touchEnd = () => {
      this.back = true;
      resetTimer();
    }

    const addEvents = () => {
      container.addEventListener("mousedown", mouseDown);
      container.addEventListener("mouseup", mouseUp);
      container.addEventListener("touchend", touchEnd);
    };
    const removeEvents = () => {
      container.removeEventListener("mousedown", mouseDown);
      container.removeEventListener("mouseup", mouseUp);
      container.removeEventListener("touchend", touchEnd);
    }

    if (container) {
      const observer = new IntersectionObserver(entries => entries.forEach(entry => entry.isIntersecting ? addEvents() : removeEvents()));
      observer.observe(container);
    } else {
      addEvents();
    }

    resetTimer();
  }

  scheduleRtrStop() {
    if (this.stopTimeoutDelay === 0) {
      this.stopRtr();
      return;
    }

    this.stopTimeoutId = setTimeout(() => {
      this.stopRtr();
    }, 5000);
  }

  window_resizeHandler = () => {
    const isDesktop = this.isDesktop();

    const bgPath = isDesktop ? this.desktopBackground : this.mobileBackground;

    const boundingArea = {
      topLeft: {
        x: 0,
        y: 0,
      },
      bottomRight: {
        x: 1,
        y: 1,
      },
    }

    this.rtrViewer.setBackgroundPath(bgPath || this.desktopBackground || this.defaultBackground);
    this.rtrViewer.setBoundingArea(boundingArea);
  }
});
